/**
 * @ra9/tan-compose-kit — battle-tested primitives built on @ra9/tan-compose.
 *
 * Each component is registered with a `tc-` prefix. Importing a component
 * module has the side-effect of registering it with `customElements`.
 * Theme tokens are NOT injected by importing the kit alone — load them
 * explicitly so you stay in control of the global cascade:
 *
 *   import "@ra9/tan-compose-kit/themes/tokens";   // default light theme
 *   // or
 *   import "@ra9/tan-compose-kit/themes/dark";     // dark mode preset
 *   import "@ra9/tan-compose-kit/themes/bootstrap"; // Bootstrap palette
 *   import "@ra9/tan-compose-kit/themes/tailwind";  // Tailwind palette
 *
 * Components fall back to hardcoded defaults if no tokens are loaded.
 */

import { tagName as buttonTag } from "./components/button.ts";
import { tagName as inputTag } from "./components/input.ts";
import { tagName as textareaTag } from "./components/textarea.ts";
import { tagName as selectTag } from "./components/select.ts";
import { tagName as checkboxTag } from "./components/checkbox.ts";
import { tagName as switchTag } from "./components/switch.ts";
import { tagName as fileTag } from "./components/file.ts";
import { tagName as radioGroupTag } from "./components/radio-group.ts";
import { tagName as tableTag } from "./components/table.ts";
import { tagName as tabsTag } from "./components/tabs.ts";
import { tagName as modalTag } from "./components/modal.ts";
import { tagName as toastTag } from "./components/toast.ts";
import { tagName as statTag } from "./components/stat.ts";
import { tagName as cardTag } from "./components/card.ts";
import { tagName as badgeTag } from "./components/badge.ts";
import { tagName as skeletonTag } from "./components/skeleton.ts";
import { tagName as stackTag } from "./components/stack.ts";
import { tagName as clusterTag } from "./components/cluster.ts";
import { tagName as gridTag } from "./components/grid.ts";
import { tagName as codeTag } from "./components/code.ts";
import { tagName as calloutTag } from "./components/callout.ts";
import { tagName as tocTag } from "./components/toc.ts";
import { tagName as paginationTag } from "./components/pagination.ts";
import { tagName as comboboxTag } from "./components/combobox.ts";
import { tagName as carouselTag } from "./components/carousel.ts";
import { tagName as accordionTag } from "./components/accordion.ts";
import { tagName as tooltipTag } from "./components/tooltip.ts";
import { tagName as popoverTag } from "./components/popover.ts";
import { tagName as drawerTag } from "./components/drawer.ts";
import { tagName as progressTag } from "./components/progress.ts";
import { tagName as stepperTag } from "./components/stepper.ts";
import { tagName as avatarTag } from "./components/avatar.ts";
import { tagName as avatarGroupTag } from "./components/avatar-group.ts";
import { tagName as ratingTag } from "./components/rating.ts";
import { tagName as sliderTag } from "./components/slider.ts";
import { tagName as chartTag } from "./components/chart.ts";
import { tagName as editorTag } from "./components/editor.ts";
import { tagName as markdownTag } from "./components/markdown.ts";

/** Tag names of every component this module registers. */
export const tags = {
  // form fields
  button: buttonTag,
  input: inputTag,
  textarea: textareaTag,
  select: selectTag,
  checkbox: checkboxTag,
  switch: switchTag,
  file: fileTag,
  radioGroup: radioGroupTag,
  slider: sliderTag,
  rating: ratingTag,
  // data
  table: tableTag,
  chart: chartTag,
  // editors
  editor: editorTag,
  markdown: markdownTag,
  // layout / chrome
  tabs: tabsTag,
  modal: modalTag,
  drawer: drawerTag,
  toast: toastTag,
  stat: statTag,
  card: cardTag,
  badge: badgeTag,
  skeleton: skeletonTag,
  carousel: carouselTag,
  accordion: accordionTag,
  tooltip: tooltipTag,
  popover: popoverTag,
  progress: progressTag,
  stepper: stepperTag,
  avatar: avatarTag,
  avatarGroup: avatarGroupTag,
  // primitives
  stack: stackTag,
  cluster: clusterTag,
  grid: gridTag,
  // docs / content
  code: codeTag,
  callout: calloutTag,
  toc: tocTag,
  pagination: paginationTag,
  // additional form fields
  combobox: comboboxTag,
} as const;
